import React, { useState, useRef } from 'react';
import { TimePeriod } from 'common/types';

import { useSelector } from 'react-redux';
import {
	Grid,
	TextField,
	Button,
	Typography,
	Box,
	InputAdornment,
	Stack,
	FormControlLabel,
	Radio,
	RadioGroup,
	Collapse,
} from '@mui/material';

import useFormField, { Form, validateForm } from 'common/hooks/useFormField';
import Container from 'common/components/Container';
import MoneyTextField from 'common/components/MoneyTextField';
import Spinner from 'common/components/Spinner';

import * as IntercomService from 'services/intercom';

import SetPageTitle from 'components/Portals/SetPageTitle';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import ContainerBox from 'components/ContainerBox';

import CategorySelect from 'views/Product/__components/CategorySelect';
import TimePeriodSelect from 'views/Product/__components/TimePeriodSelect';
import { createProduct } from 'views/Product/__utils/createProduct';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import * as UserSelectors from 'selectors/UserSelectors';

import { Routes, push, useTypedLocation } from 'routing';
import { useTranslation } from 'services/localization/useTranslation';
import { logEvent } from 'common/services/analytics';
import { setOnboardingActionDone } from 'services/onboarding/utils';
import history from 'services/history';
import ImageUpload from 'components/ImageUpload';
import { toMultiImgFormat } from 'common/utils/productUtils';

const noValidate = () => '';

type NewProductForm = {
	name: string;
	description: string;
	categoryIds: string[];
	vatPercent: number;
	set: boolean;
	locations: string[] | undefined;
	rentalsEnabled: boolean;
	rentalsBasePrice: number;
	rentalsTimePeriod: TimePeriod;
	salesEnabled: boolean;
	salesBasePrice: number;
	images: string[] | undefined;
};

const CreateProduct = () => {
	const { t } = useTranslation();
	const { state } = useTypedLocation();
	const nameFieldRef = useRef<HTMLInputElement>(null);
	const [error, setError] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);

	const shop = useSelector(ShopSelectors.activeShopData);
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const shopVatTable = useSelector(ShopSelectors.activeShopVatTable);
	const shopCurrency = useSelector(ShopSelectors.getShopCurrency);
	const shopDefaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const shopLocationsCount = useSelector(ShopSelectors.allShopLocationsCount);
	const maxProductOrderIndex = useSelector(StockSelectors.maxProductOrderIndex);
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId)!;
	const isAdmin = useSelector(UserSelectors.isAdmin);

	const activeLocationProducts = useSelector(StockSelectors.activeLocationProducts);
	const shopDeliveryOptions = useSelector(ShopSelectors.shopDeliveryOptions);
	const allProductsDeliveryOptionIds = shopDeliveryOptions
		.map((o) => o.id)
		.filter((id) =>
			activeLocationProducts.every(
				(product) => !!product.deliveryOptionIds && product.deliveryOptionIds.includes(id),
			),
		);

	const shopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);
	const shopHasCreatedProducts =
		shopAnalytics?.onboardingActions?.PRODUCT_CREATION || !!activeLocationProducts.length;

	const validateName = (name: string) => {
		if (!name || name.length < 1) {
			return t('common:validation.cannotBeEmpty', {
				field: t('common:form.name', 'Name'),
				defaultValue: '{{field}} cannot be empty',
			});
		}

		if (name.length > 200) {
			return t('common:validation.lengthAtMost', {
				productName: t('common:form.name', 'Name'),
				int: '200',
				defaultValue: '{{productName}} can be at most {{int}} characters',
			});
		}

		return;
	};

	const defaultLocations = shopLocationsCount > 1 ? [activeLocationId] : undefined;

	const form: Form<NewProductForm> = {
		name: useFormField<string>('', validateName),
		description: useFormField<string>('', noValidate),
		categoryIds: useFormField<string[]>([], noValidate),
		vatPercent: useFormField<number>(shopVatTable.defaultVat, noValidate),
		set: useFormField<boolean>(false, noValidate),
		locations: useFormField<string[] | undefined>(defaultLocations, noValidate),
		rentalsEnabled: useFormField<boolean>(true, noValidate),
		rentalsBasePrice: useFormField<number>(1000, noValidate),
		rentalsTimePeriod: useFormField<TimePeriod>('hours', noValidate),
		salesEnabled: useFormField<boolean>(false, noValidate),
		salesBasePrice: useFormField<number>(1000, noValidate),
		images: useFormField<string[] | undefined>(undefined, noValidate),
	};

	const handleSubmit = async () => {
		const { errors, values } = validateForm(form);
		if (errors.length > 0) return;

		setLoading(true);
		try {
			if (!shop) {
				throw new Error(t('common:errors.error', 'Error occured. Please try again later'));
			}
			const product = await createProduct({
				orderIndex: maxProductOrderIndex + 1,
				shop: {
					id: shop?.shopInfo.id,
					vatTable: shopVatTable,
					defaultLanguage: shopDefaultLanguage,
				},
				product: {
					name: values.name,
					description: values.description,
					categoryIds: values.categoryIds,
					vatPercent: values.vatPercent,
					set: values.set,
					locations: values.locations,
					deliveryOptionIds: allProductsDeliveryOptionIds,
					rentals: {
						enabled: values.rentalsEnabled,
						basePrice: values.rentalsBasePrice,
					},
					sales: {
						enabled: values.salesEnabled,
						basePrice: values.salesBasePrice,
					},
					rentalTimePeriod: values.rentalsTimePeriod,
					additionalProduct: !shopHasCreatedProducts ? false : true, // Used for visibility, first product created should be visible due to onboarding flow.
					images: values.images,
				},
			});
			IntercomService.trackEvent(IntercomService.CREATED_PRODUCT);
			logEvent('product_created', { set: values.set });
			if (!shopHasCreatedProducts) {
				setOnboardingActionDone('PRODUCT_CREATION', shopAnalytics);
				push(Routes.HOME_VIEW, { from: history.location.pathname });
				return;
			}
			push(Routes.ProductPage(product.id));
		} catch (e) {
			setError(e.message);
		}
		setLoading(false);
	};

	return (
		<>
			<SetPageTitle
				title={t('products.newProduct', 'New product')}
				rightContent={
					<Button
						disabled={loading}
						onClick={handleSubmit}
						variant="contained"
						color="primary"
						data-qa="createProduct"
					>
						{loading ? (
							<Spinner padding={8} thickness={4} color="white" />
						) : (
							t('product.createProduct', 'Create product')
						)}
					</Button>
				}
			/>
			<SetPageNavAction
				backAction={
					state?.from === Routes.HOME_VIEW
						? { to: Routes.HOME_VIEW, title: t('common:actions.home', 'Home') }
						: { to: Routes.CATALOG, title: t('products.products', 'Products') }
				}
			/>
			<Container densePaddingTop>
				<Grid container spacing={2}>
					<Grid item xs={12} md={8}>
						<ContainerBox>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<TextField
										inputRef={nameFieldRef}
										variant="standard"
										autoFocus
										autoComplete="off"
										fullWidth
										id="name"
										label={t('product.productName', 'Product name')}
										name={'name'}
										value={form.name.value}
										onChange={form.name.handleChange}
										margin="normal"
										type="text"
										error={Boolean(form.name.error)}
										helperText={form.name.error}
									/>
									<CategorySelect
										value={form.categoryIds.value}
										onChange={form.categoryIds.handleChange}
										disableCreate={!isAdmin}
										TextFieldProps={{
											helperText: t(
												'product.categoriesHelperText',
												'The product will be displayed under the selected categories',
											),
										}}
									/>

									<TextField
										fullWidth
										variant="outlined"
										label={t('product.description', 'Description')}
										placeholder={t(
											'product.descriptionInfo',
											'E.g. product details or usage guidelines',
										)}
										name="description"
										multiline
										rows={5}
										value={form.description.value}
										onChange={form.description.handleChange}
										margin="normal"
										type="text"
									/>
								</Grid>
							</Grid>
						</ContainerBox>
						<ContainerBox>
							<Grid container spacing={2}>
								<Grid item xs={12} md={12}>
									<Typography variant="h5" mb={1}>
										{t('product.pricing', `Pricing`)}
									</Typography>
									<Typography variant="body2" color="secondary" mb={1}>
										{t('product.pricingAddLater', `You can add more pricing options later`)}
									</Typography>
								</Grid>
								<Grid item xs={12} md={12}>
									<RadioGroup
										value={form.rentalsEnabled.value ? 'booking' : 'sales'}
										onChange={(e) => {
											const isBooking = e.target.value === 'booking';
											form.rentalsEnabled.handleChange(isBooking);
											form.salesEnabled.handleChange(!isBooking);
										}}
									>
										<FormControlLabel
											value={'booking'}
											control={<Radio />}
											label={
												<Typography sx={{ fontWeight: 500 }}>
													{t('product.bookingPrice', 'Booking price')}
												</Typography>
											}
										/>
										<Box ml={4} mb={2}>
											<Typography variant="body2" color="secondary" mb={1}>
												{t(
													'product.bookingPriceInfo',
													`Your customers can book this product for a period of time`,
												)}
											</Typography>
											<Collapse in={form.rentalsEnabled.value}>
												<MoneyTextField
													currency={shopCurrency}
													label={t('product.price', 'Price')}
													variant="standard"
													margin="normal"
													useMinorUnits
													value={form.rentalsEnabled.value ? form.rentalsBasePrice.value : null}
													onValueChange={(value) => {
														form.rentalsBasePrice.handleChange(value);
													}}
													InputProps={{
														endAdornment: form.rentalsEnabled.value && (
															<InputAdornment position="end">
																<TimePeriodSelect
																	value={form.rentalsTimePeriod.value}
																	onChange={form.rentalsTimePeriod.handleChange}
																	labelFormat="per"
																	noneLabel={t(
																		'product.perBookingFixedPrice',
																		'per booking (fixed price)',
																	)}
																	textFieldProps={{
																		variant: 'standard',
																	}}
																/>
															</InputAdornment>
														),
													}}
												/>
											</Collapse>
										</Box>

										<FormControlLabel
											value={'sales'}
											control={<Radio />}
											label={
												<Typography sx={{ fontWeight: 500 }}>
													{t('product.salesPrice', 'Sales price')}
												</Typography>
											}
										/>
										<Box ml={4}>
											<Typography variant="body2" color="secondary" mb={1}>
												{t('product.salePriceInfo', `Your customers can buy this product`)}
											</Typography>
											<Collapse in={form.salesEnabled.value}>
												<MoneyTextField
													currency={shopCurrency}
													label={t('product.price', 'Price')}
													variant="standard"
													margin="normal"
													useMinorUnits
													value={form.salesEnabled.value ? form.salesBasePrice.value : null}
													onValueChange={(value) => {
														form.salesBasePrice.handleChange(value);
													}}
												/>
											</Collapse>
										</Box>
									</RadioGroup>
								</Grid>
								<Grid item xs={12}>
									<Stack direction="column" alignItems="center" spacing={2}>
										{Boolean(error) && (
											<Typography variant="body1" color="error" align="center" gutterBottom>
												{t('common:errors.error', 'Error occured. Please try again later')}
											</Typography>
										)}
									</Stack>
								</Grid>
							</Grid>
						</ContainerBox>
					</Grid>
					<Grid item xs={12} md={4}>
						<ContainerBox>
							<ImageUpload
								value={form.images.value}
								onChange={(images) => {
									const imgUrls = toMultiImgFormat(images);
									form.images.handleChange(imgUrls);
								}}
								label={t('product.images', 'Images')}
								shopId={shopId}
								isMultiImage={true}
							/>
						</ContainerBox>
					</Grid>
				</Grid>
			</Container>
		</>
	);
};

export default CreateProduct;
